// src/domains/wallet.ts —— H5 wallet 域（原生）：余额 / 费率卡 / 资产明细五页签（U-07）、专属充币地址与本地二维码（U-08）、提币与 6 态记录（U-11）
import { ApiError } from '../../../shared/src/api/client';
import type { UserPage, WithdrawQuote } from '../../../shared/src/api/types';
import type { DcHost } from '../../../shared/src/dc';
import { cmp, fmtAmount, fromMicro, isZero, max as mmax, mulRateHalfUp, parseDecimalInput, sub, toMicro, type Micro } from '../../../shared/src/money';
import { CursorList } from '../../../shared/src/page';
import { createPoller, type Poller } from '../../../shared/src/poll';
import { qrDataUrl } from '../../../shared/src/qr';
import { fmtDateTime } from '../../../shared/src/time';
import { I18N_WALLET, REWRITES_WALLET } from '../i18n/wallet';
import type { H5State } from '../state';
import { effectiveDepositRate } from './fees';
import { listFlags } from './listview';
import { KEYS, MODAL_KEYS } from './wallet.keys';
import type { CheckoutNetwork, CheckoutQuote, DepositNet, FeeSet, LedgerRow, LedgerTab, TopupOrderRow, TopupOrderState, TopupPhase, UserWallet, WalletFees, WalletSlice, WebTopupOrder, WithdrawNet, WithdrawalRow } from './wallet.types';
import { str, type ClickEvent, type H5Ctx, type H5Domain, type InputEvent } from './types';

declare module '../state' {
  interface H5Slices {
    w: WalletSlice;
  }
}

type T = Readonly<Record<string, unknown>>;

const PAGE_SIZE = 20; // U-22 E-04
const WD_POLL_MS = 30_000; // U-22 E-05
const PRIMED_MS = 1_000; // TD-838：轮询器建立后此窗口内的首跑视为与刚完成的第 1 页重复，跳过；超出（如隐藏时建立、回前台才首跑）照常执行
const QR_SIZE = 180; // 原型 L244 二维码 CSS 尺寸（img 定宽 180px）
const TOTP_LEN = 6;
// O-27：提币金额精度与后端 money.Amount 一致（6 位小数）；展示至少 2 位、至多 6 位（去多余尾零）
const WD_DP = 6;
// sp10 D-41 拟充值金额输入：小数位 = 链上 USDT / 后端 money 精度 6 位
const DEP_AMOUNT_DP = 6;
const DEP_AMOUNT_MAX_LEN = 32; // 输入框原文截断上限（防超长串反复正则）
// 共享收款（用户裁定 2026-10-15）：用户输入基础金额 ≤ 2 位小数（与 Bot 自定义金额同口径），识别尾数由服务端自动附加；
// 订单展示精度 = 实际尾数位数（缺省 3 位；3 位用尽时服务端回退 6 位），报价阶段按缺省 3 位展示
const DEP_SHARED_INPUT_DP = 2;
const DEP_DEFAULT_TAIL_DP = 3;
// 订单页三阶段（用户批准 2026-10-17）：待支付 / 异常 15 秒刷新一次订单；已检测到付款（确认中 / 处理中）改 5 秒；已入账不再刷新
const DEP_POLL_AWAIT_S = 15;
const DEP_POLL_DETECTED_S = 5;
// 成功视图只在本订单入账后 24 小时内展示；超过后再进充值页按新单流程（避免永远停在成功页）
const DEP_SUCCESS_TTL_MS = 24 * 3600_000;
const DEP_PHASES: ReadonlySet<TopupPhase> = new Set(['awaiting', 'detected', 'processing', 'abnormal', 'credited']);
const HASH_HEAD = 8; // 交易哈希截断展示：前 8 … 后 4

export const W0: WalletSlice = {
  balance: null, feeOwed: null, depFeeRate: null, fees: null, tab: 'all', ledger: null, rev: 0, net: 'TRC20', addr: null, qr: '',
  depAmount: '', depNetworks: [], depQuote: null, depOrder: null, depOrders: null,
  wdNet: 'TRC20', wdAddr: '', wdAmount: '', wdTotp: '', wds: null, wdQuote: null,
};

// 06 U-11 收币地址校验（服务端 1104 兜底）
const ADDR_RE: Readonly<Record<WithdrawNet, RegExp>> = { TRC20: /^T[1-9A-HJ-NP-Za-km-z]{33}$/, ERC20: /^0x[0-9a-fA-F]{40}$/, BEP20: /^0x[0-9a-fA-F]{40}$/ };
const ADDR_PH: Readonly<Record<WithdrawNet, string>> = { TRC20: 'T...', ERC20: '0x...', BEP20: '0x...' };
// U-22 E-05：非终态记录
const INFLIGHT: ReadonlySet<WithdrawalRow['status']> = new Set(['pending_review', 'approved', 'paying', 'failed']);
// U-07 明细行图标（L632 六种 + 规格补全四种）
const ICON: Readonly<Record<string, string>> = {
  deposit: '↓', card_open: '▤', card_recharge: '⇢', card_withdraw: '⇠', card_close_refund: '✕', withdraw: '↑', commission_in: '♛', fee: '%', manual: '✎', revert: '↺',
};
// U-07 title_tpl → 文案键
const TPL_KEY: Readonly<Record<string, string>> = {
  deposit: 'lgDeposit', card_open: 'lgCardOpen', card_recharge: 'lgCardRecharge', card_withdraw: 'lgCardWithdraw', card_close_refund: 'lgCardCloseRefund',
  withdraw: 'lgWithdraw', commission_in: 'lgCommissionIn', fee: 'lgFee', fee_owed_settle: 'lgFeeOwedSettle', manual: 'lgManual', revert: 'lgRevert',
  deposit_reorg_hold: 'lgDepositReorgHold', // 批 3 D-16：入账后链重组风控冻结
  // 体验评审 2026-10-15 #1：充值手续费（deposit/settle.go，kind=fee）与提现激活费退回（withdrawal/activation.go，kind=fee）各有专属标题，
  // 不再回落 kind=fee 的「卡交易手续费 · {merchant}」
  deposit_fee: 'lgDepositFee', withdraw_activation_fee_refund: 'lgActRefund',
};
// 卡交易手续费以外的 kind=fee 流水：不加注「交易手续费不随退款 / 争议退回」（体验评审 #2）
const FEE_TPL_NO_TX_NOTE: ReadonlySet<string> = new Set(['fee_owed_settle', 'deposit_fee', 'withdraw_activation_fee_refund']);
// U-07 明细状态（L631 / L723 三色）
const LOG_STATUS: Readonly<Record<LedgerRow['status'], readonly [string, string]>> = {
  done: ['stDone', '#0d9a68'], pending: ['stPending', '#c47a0c'], cancel: ['stCancel', '#d1434b'],
};
// U-11 提币 6 态（P-19 / S-50 / S-55：approved 与 paying 同为 wdPaying；rejected 用原型键 wdReject）
const WD_STATUS: Readonly<Record<WithdrawalRow['status'], readonly [string, string, string]>> = {
  pending_review: ['wdReview', '#fff3df', '#c47a0c'],
  approved: ['wdPaying', '#fff3df', '#c47a0c'],
  paying: ['wdPaying', '#fff3df', '#c47a0c'],
  paid: ['wdPaid', '#e3f6ee', '#0d9a68'],
  rejected: ['wdReject', '#fdecec', '#d1434b'],
  failed: ['wdFailed', '#fff3df', '#c47a0c'],
};
// U-07 页签（原型 L722 顺序，「全部」在末位；tab 值按 X-18）
const TABS: ReadonlyArray<readonly [LedgerTab, string]> = [['deposit', 'wDeposit'], ['card', 'wRecharge'], ['withdraw', 'wWithdraw'], ['fee', 'wFee'], ['all', 'all']];
const DEP_NETS: readonly DepositNet[] = ['TRC20', 'ERC20', 'BEP20'];
// Current user withdrawals are TRC20 only; historical ERC20/BEP20 rows remain visible.
const WD_NETS: readonly WithdrawNet[] = ['TRC20'];

// 每个宿主实例一份运行期状态：轮询器与会话代号（session:ready / lost 递增，丢弃旧会话在途请求的结果）
interface Runtime {
  poll: Poller | null;
  depTimer: ReturnType<typeof setInterval> | null;
  depOffset: number;
  depBusy: boolean;
  depKey: string;
  depRequest: number;
  primedAt: number; // 建立轮询器的时刻（刚拉过第 1 页）；0 = 无待跳过的首跑
  gen: number;
  unFg: (() => void) | null;
}
const runtimes = new WeakMap<DcHost<H5State>, Runtime>();
function rt(host: DcHost<H5State>): Runtime {
  let r = runtimes.get(host);
  if (!r) {
    r = { poll: null, depTimer: null, depOffset: 0, depBusy: false, depKey: '', depRequest: 0, primedAt: 0, gen: 0, unFg: null };
    runtimes.set(host, r);
  }
  return r;
}

function setW(ctx: H5Ctx, patch: Partial<WalletSlice>): void {
  ctx.host.setState({ w: { ...ctx.host.state.w, ...patch } });
}

function bump(ctx: H5Ctx): void {
  setW(ctx, { rev: ctx.host.state.w.rev + 1 });
}

// 金额解析与展示：服务端 / 配置值畸形时记日志并降级，不让整页渲染抛错（fmtAmount / toMicro 对畸形值抛错）
function micro(v: string | number | null | undefined): Micro | null {
  if (v === null || v === undefined || v === '') return null;
  try {
    return toMicro(String(v));
  } catch (e) {
    console.error('[gp:wallet] 金额格式非法', v, e);
    return null;
  }
}

function m2(v: string | number | null | undefined): string {
  const x = micro(v);
  return x === null ? '0.00' : fmtAmount(x, 2);
}

// O-27：6 位精度金额展示——截断到 6 位后去掉第 3～6 位多余尾零（"1.330000" → "1.33"，"1.333300" → "1.3333"）
function m6(v: string | Micro | null | undefined): string {
  const x = typeof v === 'bigint' ? v : micro(v);
  if (x === null) return '0.00';
  return fmtAmount(x, 6).replace(/(\.\d\d\d*?)0+$/, '$1');
}

// 充值订单展示精度步长（用于应付金额定长展示）。dp ∈ [0, 6]。
function dpStep(dp: number): Micro {
  return 10n ** BigInt(6 - Math.min(6, Math.max(0, dp)));
}
// 报价值展示（用户拍板 2026-10-15）：服务端报价原值去尾零，不做任何向上 / 向下取整——充值手续费已由服务端按 0.001 HALF_UP 定值
// （backend fx.DepositFee），展示值 = 报价值 = 入账实扣值（0.105000 → 0.105，0 → 0）
function quoteAmt(v: string | null | undefined): string {
  const x = micro(v);
  return x === null ? '' : trimZeros(fmtAmount(x, 6, { group: false }));
}
// payAmount：应付精确金额按尾数位数定长展示（"50.001000" → "50.001"）；dp 之后若有非零位（不应发生）原样 6 位，绝不舍入
function payAmount(v: string, dp: number): string {
  const x = micro(v);
  if (x === null) return v;
  return x % dpStep(dp) === 0n ? fmtAmount(x, dp, { group: false }) : fmtAmount(x, 6, { group: false });
}
// tailDp：订单 / 报价展示精度——共享订单取实际 precision_digits（3 / 6），报价阶段按缺省 3 位；旧版每人地址订单 6 位
function tailDp(shared: boolean, digits: number | undefined): number {
  if (!shared) return 6;
  return digits === 3 || digits === 6 ? digits : DEP_DEFAULT_TAIL_DP;
}

function trimZeros(s: string): string {
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

// 百分数展示：最多 4 位小数，去尾零（"2.500000" → "2.5"）
function pct(v: string): string {
  const x = micro(v);
  return x === null ? '0' : trimZeros(fmtAmount(x, 4, { group: false }));
}

// 文案内嵌限额（{min} / {min_deposit}）：两位截断后去尾零，与原型「最小提币 20 USDT」「最小充值 10 USDT」同形
function lim(v: string): string {
  return trimZeros(m2(v));
}

function interp(tpl: string, p: Readonly<Record<string, string | number>>): string {
  return tpl.replace(/\{(\w+)\}/g, (m, k: string) => {
    const v = p[k];
    return v === undefined || v === null ? m : String(v);
  });
}

function sameGen(ctx: H5Ctx, gen: number): boolean {
  return rt(ctx.host).gen === gen;
}

// 体验评审 2026-10-15 #1：流水标题缺参（title_params 无对应键或为空串）时降级为无参文案——连同其前置分隔符（「 · 」或空白）一并去掉，
// 绝不把字面占位符（如「{merchant}」）外露给用户
function interpTitle(tpl: string, p: Readonly<Record<string, string | number>>): string {
  return tpl
    .replace(/(\s*·\s*|\s+)?\{(\w+)\}/g, (_m, sep: string | undefined, k: string) => {
      const v = p[k];
      return v === undefined || v === null || v === '' ? '' : (sep ?? '') + String(v);
    })
    .trim();
}

export function ledgerTitle(t: T, r: LedgerRow): string {
  const key = TPL_KEY[r.title_tpl ?? r.kind] ?? TPL_KEY[r.kind] ?? '';
  const tpl = key ? str(t, key) : '';
  return tpl ? interpTitle(tpl, r.title_params ?? {}) || r.kind : r.kind;
}

// S-03：kind=fee 且 title_params.owed > 0 时的欠费增量（展示两位）
function owedOf(r: LedgerRow): string | null {
  if (r.kind !== 'fee') return null;
  const raw = r.title_params?.owed;
  const x = raw === undefined ? null : micro(raw);
  return x !== null && x > 0n ? fmtAmount(x, 2) : null;
}

// 零额展示不带符号（0.00 USDT），其余按方向 ± 保留真实位数（至少两位；用户拍板 2026-10-15：充值手续费 0.105 不得显示为 0.10）
function ledgerAmount(r: LedgerRow): string {
  const neg = r.amount.trim().startsWith('-');
  const abs = r.amount.trim().replace(/^[-+]/, '');
  const x = micro(abs);
  if (x === null || isZero(x)) return '0.00 USDT';
  return (neg ? '-' : '+') + m6(x) + ' USDT';
}

// 二维码按设备像素比生成位图（CSS 定宽不变），高 DPR 屏不糊；上限 3 倍控制 data URL 体积
function qrPixels(css: number): number {
  const dpr = typeof window !== 'undefined' && typeof window.devicePixelRatio === 'number' && Number.isFinite(window.devicePixelRatio) ? window.devicePixelRatio : 1;
  return css * Math.min(3, Math.max(1, Math.ceil(dpr)));
}

type FeeRow = { label: string; value: string };
type UsageFees = Pick<FeeSet, 'tx_fee_rate' | 'tx_fee_fixed' | 'cross_border_rate' | 'cross_border_fixed' | 'declined_fee' | 'dispute_fee'>;

// 金额 / 费率字符串为 0（或畸形，按 0 处理，与 m2 / pct 回落一致）
function zeroish(v: string): boolean {
  const x = micro(v);
  return x === null || isZero(x);
}

// U-07 我的卡费率（用户裁定 2026-10-15）：只列用卡环节费用。消费手续费恒列（0 显示「免费」），
// 跨境 / 拒付 / 争议按 D14 零值规则省略。销卡费不在此汇总，只在销卡确认（U-06c closeSub）与卡详情（U-05）展示。
export function feeRowsOf(t: T, f: UsageFees): FeeRow[] {
  const free = str(t, 'feeFree');
  const rows: FeeRow[] = [
    { label: str(t, 'txFee'), value: zeroish(f.tx_fee_rate) && zeroish(f.tx_fee_fixed) ? free : `${pct(f.tx_fee_rate)}% + ${m2(f.tx_fee_fixed)} USDT` },
  ];
  // 用户拍板 2026-10-15：手续费从 USDT 余额扣，一律写 USDT
  if (!(zeroish(f.cross_border_rate) && zeroish(f.cross_border_fixed))) rows.push({ label: str(t, 'crossBorderFee'), value: `${pct(f.cross_border_rate)}% + ${m2(f.cross_border_fixed)} USDT` });
  if (!zeroish(f.declined_fee)) rows.push({ label: str(t, 'declinedFee'), value: m2(f.declined_fee) + ' USDT' });
  if (!zeroish(f.dispute_fee)) rows.push({ label: str(t, 'disputeFee'), value: m2(f.dispute_fee) + ' USDT' });
  return rows;
}

// 平台通用费率：增加额度恒免费（D33）+ 卡转出固定费（全局键 cardWithdrawFee，0 显示「免费」）+ 平台默认用卡费率
export function defaultFeeRowsOf(t: T, f: UsageFees & { card_withdraw_fee: string }): FeeRow[] {
  const free = str(t, 'feeFree');
  return [
    { label: str(t, 'feeAddLimit'), value: free },
    { label: str(t, 'feeCardWd'), value: zeroish(f.card_withdraw_fee) ? free : m2(f.card_withdraw_fee) + ' USDT' },
    ...feeRowsOf(t, f),
  ];
}

function limit(s: H5State, k: string, dflt: string): string {
  const v = s.sh.config?.limits[k];
  return v === undefined || v === null || v === '' ? dflt : String(v);
}

function withdrawRate(s: H5State): string {
  return String(s.sh.config?.fees.withdraw_fee_rate ?? '0');
}

// TRC20 激活服务费（config.limits.fee_trc_activation，P-17 / 评审 M-6）；非 TRC20、缺省、畸形或 ≤ 0 视为不收
function wdActFee(s: H5State): Micro {
  const act = micro(limit(s, 'fee_trc_activation', '0'));
  return s.w.wdNet === 'TRC20' && act !== null && act > 0n ? act : 0n;
}

// 提现费附注：比例费率（F-06）+ TRC20 激活服务费（收款地址未激活另收；平台未实际激活则付款核验后退回）。
// 激活与否由服务端申请时判定、前端无从预知，故体验评审 #14 / 2026-10-15 协调口径：已填金额时直接给出「需激活时的实际到账 = 金额 − 提现手续费 − 激活费」
// 2026-10-15：有与当前输入匹配的预检结果时不再出笼统激活费附注（改由 wdActTip 醒目提示确定结论），仅无预检结果时回落笼统口径。
function wdNotes(s: H5State, t: Parameters<typeof str>[0], rateM: Micro | null, rate: string, netIfAct: Micro | null): string {
  const notes: string[] = [];
  if (rateM !== null && rateM > 0n) notes.push(interp(str(t, 'wdFeeNote'), { rate: pct(rate) }));
  const act = wdActFee(s);
  if (act > 0n && currentWdQuote(s) === null) notes.push(interp(str(t, netIfAct === null ? 'wdActNote' : 'wdActNoteNet'), { fee: m6(act), net: netIfAct === null ? '' : m6(netIfAct) }));
  return notes.join(' · ');
}

// 预检提示金额：去尾零、不分组（"2.500000" → "2.5"，"26.000000" → "26"）
function t0(v: string): string {
  const x = micro(v);
  return x === null ? v : trimZeros(fmtAmount(x, 6, { group: false }));
}

// wdActTip：预检确定未激活 → 醒目提示激活费与实际到账；不确定 → 提示可能另收；已激活 → 无提示
function wdActTip(t: Parameters<typeof str>[0], q: WithdrawQuote | null): string {
  if (q === null) return '';
  if (q.activation_check === 'unknown') return interp(str(t, 'wdActUnknown'), { fee: t0(q.activation_fee) });
  if (q.needs_activation) return interp(str(t, 'wdActNeeded'), { activation_fee: t0(q.activation_fee), net: t0(q.net_amount) });
  return '';
}

// F-06：提现手续费 = 固定网络费（fee_trc 默认 1 / fee_erc 默认 8）+ HALF_UP₆(amount × withdraw_fee_rate%)；金额未填 / 非法只计矿工费
export function wdFee(s: H5State, amount: string): Micro {
  const miner = micro(s.w.wdNet === 'TRC20' ? limit(s, 'fee_trc', '1') : s.w.wdNet === 'BEP20' ? limit(s, 'fee_bep', '0.5') : limit(s, 'fee_erc', '8')) ?? 0n;
  const amt = parseDecimalInput(amount, WD_DP);
  const rate = micro(withdrawRate(s)) === null ? '0' : withdrawRate(s);
  return amt ? miner + mulRateHalfUp(toMicro(amt), rate) : miner;
}

// sp10 D-41 / 0045：入金手续费率（%）。登录态优先用户生效价 GET /wallet.deposit_fee_rate，其次 /wallet/fees.default.deposit_fee_rate，
// 均缺省才回落平台价 config.fees.deposit_fee_rate（D33）。缺省 / 畸形 / 越界（< 0 或 ≥ 100）返回 null：
// 页面显示「—」且不出预览，绝不回落 0（回落 0 即重现「手续费固定 0」与实收不符的缺陷）
export function depositRate(s: H5State): string | null {
  const own = s.w.depFeeRate ?? s.w.fees?.default.deposit_fee_rate ?? null;
  return effectiveDepositRate(own, s.sh.config ?? null);
}

// 欠费以 /wallet.fee_owed 为准，未取到时回落 /profile.fee_owed（06 U-07 / U-11）
function owedNow(s: H5State): string {
  return s.w.feeOwed ?? s.sh.profile?.fee_owed ?? '0';
}

function errNet(ctx: H5Ctx, e: unknown): void {
  ctx.gp.ui.toast(ctx.gp.ui.err(e), 'error');
}

async function loadWallet(ctx: H5Ctx): Promise<void> {
  const gen = rt(ctx.host).gen;
  const g = ctx.gp;
  try {
    const [w, fees] = await Promise.all([g.api.call<UserWallet>('GET /api/user/wallet'), g.api.call<WalletFees>('GET /api/user/wallet/fees')]);
    if (sameGen(ctx, gen)) setW(ctx, { balance: w.balance, feeOwed: w.fee_owed, depFeeRate: w.deposit_fee_rate ?? null, fees });
  } catch (e) {
    if (sameGen(ctx, gen)) errNet(ctx, e);
  }
}

function newList<R>(ctx: H5Ctx, route: 'GET /api/user/wallet/ledger' | 'GET /api/user/wallet/withdrawals', extra: Readonly<Record<string, string>>): CursorList<R> {
  return new CursorList<R>((page) => ctx.gp.api.call<UserPage<R>>(route, { query: { ...extra, page, size: PAGE_SIZE } }));
}

// 资产明细「充币」页签（用户裁定 2026-10-15）：列本人充值订单（待支付 / 充币成功 / 充币失败-超时 …），不再列 deposit 入账流水——
// 已到账订单与入账流水同一笔钱，只在本页签按订单展示一次；「全部」页签仍按账本流水展示（含充值入账），两页签各自不重复。
function newOrderList(ctx: H5Ctx): CursorList<TopupOrderRow> {
  return new CursorList<TopupOrderRow>(async (page) => {
    const out = await ctx.gp.api.call<UserPage<TopupOrderRow>>('GET /api/user/wallet/topup-orders', { query: { page, size: PAGE_SIZE } });
    const at = Date.now();
    return { ...out, list: out.list.map((o) => ({ ...o, deadline_ms: o.state === 'pending' ? at + Math.max(0, o.remaining_seconds ?? 0) * 1000 : undefined })) };
  });
}

async function loadLedger(ctx: H5Ctx, tab: LedgerTab): Promise<void> {
  if (tab === 'deposit') {
    const depOrders = newOrderList(ctx);
    setW(ctx, { tab, ledger: null, depOrders });
    await depOrders.loadFirst();
    if (ctx.host.state.w.depOrders !== depOrders) return;
    bump(ctx);
    if (depOrders.error) ctx.gp.ui.toast(str(ctx.t, 'errNetwork'), 'error');
    return;
  }
  const ledger = newList<LedgerRow>(ctx, 'GET /api/user/wallet/ledger', { tab });
  setW(ctx, { tab, ledger, depOrders: null });
  await ledger.loadFirst();
  if (ctx.host.state.w.ledger !== ledger) return; // 已切页签 / 会话重置：丢弃
  bump(ctx);
  if (ledger.error) ctx.gp.ui.toast(str(ctx.t, 'errNetwork'), 'error');
}

async function loadMoreOf(ctx: H5Ctx, key: 'logMore' | 'wdMore', pick: (w: WalletSlice) => CursorList<LedgerRow> | CursorList<WithdrawalRow> | CursorList<TopupOrderRow> | null): Promise<void> {
  const list = pick(ctx.host.state.w);
  if (!list || !list.hasMore) return;
  await ctx.gp.ui.run(key, async () => {
    await list.loadMore();
    if (pick(ctx.host.state.w) !== list) return;
    bump(ctx);
    if (list.error) ctx.gp.ui.toast(str(ctx.t, 'errNetwork'), 'error'); // E-04：加载更多失败 toast，按钮保留可重试
  });
}

function orderStorageKey(ctx: H5Ctx): string | null {
  const uid = ctx.host.state.sh.profile?.uid;
  return uid ? `gpu_deposit_order_${uid}` : null;
}

interface PendingDepositOrder { precision_digits?: 3 | 6; network: DepositNet; amount: string; idempotency_key: string; quote_fingerprint: string }

function pendingOrderStorageKey(ctx: H5Ctx): string | null {
  const uid = ctx.host.state.sh.profile?.uid;
  return uid ? `gpu_deposit_pending_${uid}` : null;
}

function pendingDepositOrder(ctx: H5Ctx): PendingDepositOrder | null {
  const key = pendingOrderStorageKey(ctx);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const p: unknown = JSON.parse(raw);
    if (typeof p !== 'object' || p === null) return null;
    const v = p as Record<string, unknown>;
    if (!DEP_NETS.includes(v.network as DepositNet) || typeof v.amount !== 'string' || typeof v.idempotency_key !== 'string' || typeof v.quote_fingerprint !== 'string') return null;
    if (v.precision_digits!==undefined && v.precision_digits!==3 && v.precision_digits!==6) return null;
    return v as unknown as PendingDepositOrder;
  } catch { return null; }
}

function clearPendingDepositOrder(ctx: H5Ctx): void {
  const key = pendingOrderStorageKey(ctx);
  if (key) localStorage.removeItem(key);
  rt(ctx.host).depKey = '';
}

// 订单页阶段：服务端 phase（backend deposit/checkout_phase.go）；旧后端缺省 / 未知值按 awaiting（原支付页 + 本订单入账小字）
function orderPhase(o: WebTopupOrder | null): TopupPhase {
  return o?.phase && DEP_PHASES.has(o.phase) ? o.phase : 'awaiting';
}

// 订单刷新间隔（秒）；null = 不再刷新（已入账为终态）
function depPollSecs(o: WebTopupOrder): number | null {
  const p = orderPhase(o);
  if (p === 'credited') return null;
  return p === 'detected' || p === 'processing' ? DEP_POLL_DETECTED_S : DEP_POLL_AWAIT_S;
}

// 成功视图是否已过 24 小时展示窗口：以服务端时刻为准（不依赖本机时钟）；入账时间缺省时按建单时间
function successExpired(o: WebTopupOrder): boolean {
  if (orderPhase(o) !== 'credited') return false;
  const at = Date.parse(o.payment?.credited_at ?? o.created_at);
  const now = Date.parse(o.server_time);
  if (!Number.isFinite(at) || !Number.isFinite(now)) return false;
  return now - at > DEP_SUCCESS_TTL_MS;
}

// 放弃当前订单回到新建流程：清本地订单键 / 未决建单与页面状态，停刷新（「创建新充值订单」「继续充值」、成功视图过期共用）
function resetDepositOrder(ctx: H5Ctx): void {
  clearPendingDepositOrder(ctx);
  const key = orderStorageKey(ctx);
  if (key) localStorage.removeItem(key);
  setW(ctx, { depOrder: null, depQuote: null, depAmount: '', qr: '' });
  stopDepositTimer(ctx.host);
}

function stopDepositTimer(host: DcHost<H5State>): void {
  const r = rt(host);
  if (r.depTimer) clearInterval(r.depTimer);
  r.depTimer = null;
}

function chooseDepositNetwork(ctx: H5Ctx, net: DepositNet): void {
  if (ctx.host.state.w.depOrder || ctx.host.state.w.net === net) return;
  rt(ctx.host).depRequest++;
  rt(ctx.host).depKey = '';
  setW(ctx, { net, depQuote: null, qr: '' });
}

function depositInputAmount(raw: string): Micro | null {
  if (!/^[0-9]+(?:\.[0-9]{1,6})?$/.test(raw)) return null;
  try { return toMicro(raw); } catch { return null; }
}

function editDepositAmount(ctx: H5Ctx, text: string): void {
  rt(ctx.host).depRequest++;
  rt(ctx.host).depKey = '';
  setW(ctx, { depAmount: text.slice(0, DEP_AMOUNT_MAX_LEN), depQuote: null });
}

async function loadDepositNetworks(ctx: H5Ctx): Promise<void> {
  if (ctx.host.state.sh.config?.switches.deposit === false) return;
  const gen = rt(ctx.host).gen;
  try {
    const depNetworks = await ctx.gp.api.call<CheckoutNetwork[]>('GET /api/user/wallet/deposit-networks');
    if (sameGen(ctx, gen)) setW(ctx, { depNetworks });
  } catch (e) {
    if (sameGen(ctx, gen)) errNet(ctx, e);
  }
}

// 充值不可用原因 → 文案键（审计 #23）：网络列表 reason 与报价 / 建单的 1503 / 1012 reason 同口径；未知 reason 落到通用「网络不可用」。
const DEP_REASON_KEYS: Readonly<Record<string, string>> = {
  no_healthy_approved_address: 'depPoolUnavailable',
  amount_pool_exhausted: 'depAmountPoolExhausted',
  deposit_order_limit: 'depOrderLimit',
  legacy_address_retired: 'depLegacyRetired',
};

function depReasonKey(reason: string | undefined): string {
  return (reason && DEP_REASON_KEYS[reason]) || 'depNetworkUnavailable';
}

// depErrText：充值报价 / 建单失败文案——带已知 reason 的业务拒绝给具体原因，其余走通用错误映射。
function depErrText(ctx: H5Ctx, e: unknown): string {
  const reason = e instanceof ApiError ? e.data.reason : undefined;
  return reason && DEP_REASON_KEYS[reason] ? str(ctx.t, DEP_REASON_KEYS[reason]) : ctx.gp.ui.err(e);
}

async function requestDepositQuote(ctx: H5Ctx): Promise<void> {
  const s = ctx.host.state;
  if (s.busy || s.w.depOrder || s.sh.config?.switches.deposit === false) return;
  if (ctx.gp.gate.guard()) return; // U-24：未开通邮箱登录不报价建单，改弹引导
  const shared=s.w.depNetworks.find((n)=>n.network===s.w.net)?.address_mode==='shared';
  // 共享收款：用户只输入基础金额（≤ 2 位小数），识别尾数由服务端建单时自动附加（3 位优先，用尽回退 6 位）
  const amount = parseDecimalInput(s.w.depAmount, shared ? DEP_SHARED_INPUT_DP : DEP_AMOUNT_DP);
  const min = s.w.depNetworks.find((n) => n.network === s.w.net)?.min_deposit_usdt;
  const value = depositInputAmount(s.w.depAmount);
  if (!amount || value === null || value <= 0n || (min && value < (micro(min) ?? 0n))) {
    ctx.gp.ui.toast(str(ctx.t, shared ? 'depInvalidAmountShared' : 'depInvalidAmount'), 'error');
    return;
  }
  const net = s.w.net;
  const request = ++rt(ctx.host).depRequest;
  const gen = rt(ctx.host).gen;
  await ctx.gp.ui.run('depQuote', async () => {
    try {
      const depQuote = await ctx.gp.api.call<CheckoutQuote>('POST /api/user/wallet/deposit-quote', { body: { network: net, amount: canonAmount(amount) } });
      if (sameGen(ctx, gen) && rt(ctx.host).depRequest === request && ctx.host.state.w.net === net) setW(ctx, { depQuote });
    } catch (e) {
      if (sameGen(ctx, gen)) ctx.gp.ui.toast(depErrText(ctx, e), 'error');
    }
  });
}

async function acceptDepositOrder(ctx: H5Ctx, order: WebTopupOrder): Promise<void> {
  const r = rt(ctx.host);
  r.depOffset = Date.parse(order.server_time) - Date.now();
  const prev = ctx.host.state.w.depOrder;
  const qr = prev?.address === order.address ? ctx.host.state.w.qr : '';
  setW(ctx, { depOrder: order, net: order.network, depAmount: order.amount_usdt, depQuote: null, qr });
  const storageKey = orderStorageKey(ctx);
  if (storageKey) localStorage.setItem(storageKey, String(order.id));
  clearPendingDepositOrder(ctx);
  const gen = r.gen;
  if (ctx.host.state.screen === 'deposit') startDepositTimer(ctx);
  // 本页内由未入账切到已入账：重拉余额（首页 / 开卡表单 / 钱包读 usdtBalance），成功视图「查看余额」即见新值
  if (orderPhase(order) === 'credited' && prev?.id === order.id && orderPhase(prev) !== 'credited') void loadWallet(ctx);
  // 二维码只在待支付阶段展示；已检测到付款 / 成功 / 异常不生成（防重复付款）
  if (!qr && orderPhase(order) === 'awaiting') {
    try {
      const generated = await qrDataUrl(order.address, qrPixels(QR_SIZE));
      if (sameGen(ctx, gen) && ctx.host.state.w.depOrder?.id === order.id) setW(ctx, { qr: generated });
    } catch (e) { console.error('[gp:wallet] 充值二维码生成失败', e); }
  }
}

async function refreshDepositOrder(ctx: H5Ctx, id: number): Promise<void> {
  const gen = rt(ctx.host).gen;
  try {
    const order = await ctx.gp.api.call<WebTopupOrder>('GET /api/user/wallet/topup-orders/{id}', { path: { id } });
    if (sameGen(ctx, gen) && ctx.host.state.screen === 'deposit') await acceptDepositOrder(ctx, order);
  } catch (e) {
    if (!sameGen(ctx, gen)) return;
    if (e instanceof ApiError && e.status === 404) {
      const storageKey = orderStorageKey(ctx);
      if (storageKey) localStorage.removeItem(storageKey);
      setW(ctx, { depOrder: null, qr: '' });
    } else errNet(ctx, e);
  }
}

function startDepositTimer(ctx: H5Ctx): void {
  const r = rt(ctx.host);
  if (r.depTimer) return;
  let ticks = 0;
  // 每秒刷新倒计时；按阶段间隔拉订单（待支付 / 异常 15 秒，确认中 / 处理中 5 秒，已入账不拉）。阶段切换后下一拍即按新间隔
  r.depTimer = setInterval(() => {
    const o = ctx.host.state.w.depOrder;
    if (ctx.host.state.screen !== 'deposit' || !o) return;
    bump(ctx);
    const every = depPollSecs(o);
    if (every === null) return;
    if (++ticks >= every) {
      ticks = 0;
      void refreshDepositOrder(ctx, o.id);
    }
  }, 1000);
}

async function openDeposit(ctx: H5Ctx): Promise<void> {
  if (ctx.host.state.sh.config?.switches.deposit === false) return;
  void loadDepositNetworks(ctx);
  const key = orderStorageKey(ctx);
  const id = key ? Number(localStorage.getItem(key)) : 0;
  if (Number.isSafeInteger(id) && id > 0) await refreshDepositOrder(ctx, id);
  // 再次进入：按当前阶段渲染；成功视图只保留入账后 24 小时，过期即丢弃本地订单键、回到新建流程
  const cur = ctx.host.state.w.depOrder;
  if (cur && successExpired(cur)) resetDepositOrder(ctx);
  if (!ctx.host.state.w.depOrder) {
    const pending = pendingDepositOrder(ctx);
    if (pending) await submitDepositOrder(ctx, pending);
  }
}

async function submitDepositOrder(ctx: H5Ctx, pending: PendingDepositOrder): Promise<void> {
  const r = rt(ctx.host);
  if (r.depBusy || ctx.host.state.w.depOrder) return;
  const gen = r.gen;
  r.depBusy = true;
  try {
    const order = await ctx.gp.api.call<WebTopupOrder>('POST /api/user/wallet/topup-orders', { body: pending });
    if (sameGen(ctx, gen)) await acceptDepositOrder(ctx, order);
  } catch (e) {
    if (sameGen(ctx, gen) && e instanceof ApiError && e.code === 1110 && e.data.refreshed_quote) {
      setW(ctx, { depQuote: e.data.refreshed_quote as CheckoutQuote });
      clearPendingDepositOrder(ctx);
      ctx.gp.ui.toast(str(ctx.t, 'depQuoteChanged'), 'error');
    } else if (sameGen(ctx, gen)) ctx.gp.ui.toast(depErrText(ctx, e), 'error');
  } finally {
    r.depBusy = false;
  }
}

async function confirmDepositOrder(ctx: H5Ctx): Promise<void> {
  const w = ctx.host.state.w;
  const r = rt(ctx.host);
  if (r.depBusy || w.depOrder || ctx.host.state.sh.config?.switches.deposit === false) return;
  if (ctx.gp.gate.guard()) return; // U-24
  const saved = pendingDepositOrder(ctx);
  if (saved) { await submitDepositOrder(ctx, saved); return; }
  if (!w.depQuote) return;
  if (!r.depKey) r.depKey = `web_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  const pending: PendingDepositOrder = { network: w.depQuote.network, amount: w.depQuote.amount_usdt, idempotency_key: r.depKey, quote_fingerprint: w.depQuote.fingerprint, ...(w.depQuote.precision_digits ? { precision_digits: w.depQuote.precision_digits } : {}) };
  const key = pendingOrderStorageKey(ctx);
  if (!key) return;
  localStorage.setItem(key, JSON.stringify(pending));
  await submitDepositOrder(ctx, pending);
}

async function loadWds(ctx: H5Ctx): Promise<void> {
  const wds = newList<WithdrawalRow>(ctx, 'GET /api/user/wallet/withdrawals', {});
  setW(ctx, { wds });
  await wds.loadFirst();
  if (ctx.host.state.w.wds !== wds) return;
  bump(ctx);
  if (wds.error) ctx.gp.ui.toast(str(ctx.t, 'errNetwork'), 'error');
  manageWdPoll(ctx);
}

// 轮询 tick：重拉第 1 页并替换列表头部；列表已被 loadFirst 替换 / 首屏未完成时丢弃本次结果
async function pollTick(ctx: H5Ctx): Promise<void> {
  const r = rt(ctx.host);
  if (r.primedAt > 0) {
    const fresh = Date.now() - r.primedAt < PRIMED_MS;
    r.primedAt = 0;
    if (fresh) return;
  }
  const list = ctx.host.state.w.wds;
  if (!list || list.firstLoading) return;
  const first = await ctx.gp.api.call<UserPage<WithdrawalRow>>('GET /api/user/wallet/withdrawals', { query: { page: 1, size: PAGE_SIZE } });
  if (ctx.host.state.w.wds !== list || list.firstLoading || !rt(ctx.host).poll) return;
  const ids = new Set(first.list.map((x) => x.id));
  list.items = first.list.concat(list.items.slice(PAGE_SIZE).filter((x) => !ids.has(x.id)));
  if (list.page <= 1) list.hasMore = first.has_more;
  bump(ctx);
  manageWdPoll(ctx);
}

function stopPoll(host: DcHost<H5State>): void {
  const r = rt(host);
  r.poll?.stop();
  r.poll = null;
}

function manageWdPoll(ctx: H5Ctx): void {
  const s = ctx.host.state;
  const r = rt(ctx.host);
  const need = s.screen === 'withdrawUsdt' && (s.w.wds?.items ?? []).some((x) => INFLIGHT.has(x.status));
  if (need && !r.poll) {
    r.primedAt = Date.now(); // 仅在 loadWds 之后创建：第 1 页刚拉过
    r.poll = createPoller(() => pollTick(ctx), WD_POLL_MS);
    r.poll.start();
  } else if (!need && r.poll) {
    stopPoll(ctx.host);
  }
}

// 提交金额规范化：去前导零（parseDecimalInput 不规范化，"007.5" → "7.5"）
function canonAmount(a: string): string {
  return a.replace(/^0+(?=\d)/, '');
}

// ── 提现预检（2026-10-15）：地址与金额都合法后防抖调用 GET /wallet/withdrawals/quote，由服务端经 Tatum 读链判定收款地址是否已激活。
// 竞态：每次输入变化递增 seq 并中止在途请求，只认最后一次请求的结果；结果带 key（网络|金额|地址），与当前输入不一致即不展示。
const WDQ_DEBOUNCE_MS = 500;
interface WdqRuntime {
  timer: ReturnType<typeof setTimeout> | null;
  ac: AbortController | null;
  seq: number;
  key: string; // 最近一次已调度 / 已取回的预检键；'' = 当前输入不满足预检条件
}
const wdqRuntimes = new WeakMap<DcHost<H5State>, WdqRuntime>();
function wdq(host: DcHost<H5State>): WdqRuntime {
  let r = wdqRuntimes.get(host);
  if (!r) {
    r = { timer: null, ac: null, seq: 0, key: '' };
    wdqRuntimes.set(host, r);
  }
  return r;
}

// cancelWdQuote：清掉防抖定时器并中止在途预检（seq 递增使迟到结果作废）
function cancelWdQuote(host: DcHost<H5State>): void {
  const r = wdq(host);
  r.seq++;
  if (r.timer !== null) clearTimeout(r.timer);
  r.timer = null;
  r.ac?.abort();
  r.ac = null;
  r.key = '';
}

// wdQuoteKey：满足预检条件（TRC20、地址格式合法、金额合法且 ≥ 最小提现额、提现开关开、激活费配置 > 0）时返回键，否则 ''
function wdQuoteKey(s: H5State): string {
  const w = s.w;
  if (w.wdNet !== 'TRC20' || s.sh.config?.switches.withdraw === false || wdActFee(s) === 0n) return '';
  const addr = w.wdAddr.trim();
  if (!ADDR_RE[w.wdNet].test(addr)) return '';
  const amt = parseDecimalInput(w.wdAmount, WD_DP);
  if (!amt) return '';
  const m = toMicro(amt);
  if (m <= 0n || cmp(m, micro(limit(s, 'min_withdraw', '20')) ?? 0n) < 0) return '';
  return `${w.wdNet}|${canonAmount(amt)}|${addr}`;
}

// currentWdQuote：与当前输入匹配的预检结果（键不一致 = 过期，不展示）
function currentWdQuote(s: H5State): WithdrawQuote | null {
  const key = wdQuoteKey(s);
  return key !== '' && s.w.wdQuote?.key === key ? s.w.wdQuote.q : null;
}

// scheduleWdQuote：输入变化后调用。键未变不重复请求；键变化先清旧结果（切换地址 / 金额不残留旧判定），合法时防抖发起
function scheduleWdQuote(ctx: H5Ctx): void {
  const host = ctx.host;
  const key = wdQuoteKey(host.state);
  const r = wdq(host);
  if (key === r.key) return;
  cancelWdQuote(host);
  if (host.state.w.wdQuote) setW(ctx, { wdQuote: null });
  if (key === '') return;
  r.key = key;
  const seq = r.seq;
  r.timer = setTimeout(() => void fetchWdQuote(ctx, key, seq), WDQ_DEBOUNCE_MS);
}

async function fetchWdQuote(ctx: H5Ctx, key: string, seq: number): Promise<void> {
  const r = wdq(ctx.host);
  if (r.seq !== seq) return;
  r.timer = null;
  const [network, amount, address] = key.split('|');
  const ac = new AbortController();
  r.ac = ac;
  try {
    const q = await ctx.gp.api.call<WithdrawQuote>('GET /api/user/wallet/withdrawals/quote', { query: { network, address, amount }, signal: ac.signal });
    if (r.seq !== seq || wdQuoteKey(ctx.host.state) !== key) return;
    setW(ctx, { wdQuote: { key, q } });
  } catch (e) {
    if (r.seq !== seq || (e instanceof DOMException && e.name === 'AbortError')) return;
    // 预检失败（限速 / 地址被拒 / 网络错误）不打断填写：回落笼统激活费提示，权威校验仍在提交时
    console.warn('[gp:wallet] 提现预检失败，回落笼统提示', e);
    r.key = ''; // 允许同一输入稍后重试（下一次输入变化即重发）
  } finally {
    if (r.ac === ac) r.ac = null;
  }
}

async function submitWd(ctx: H5Ctx): Promise<void> {
  const s = ctx.host.state;
  const t = ctx.t;
  const w = s.w;
  const ui = ctx.gp.ui;
  if (s.busy) return;
  const minWd = limit(s, 'min_withdraw', '20');
  const vars = { min: lim(minWd), fee_owed: m2(owedNow(s)) };
  if (s.sh.config?.switches.withdraw === false) return ui.toast(str(t, 'errSwitchOff'), 'error');
  if (w.wdNet !== 'TRC20') return ui.toast(str(t, 'errWdTrcOnly'), 'error');
  const addr = w.wdAddr.trim();
  if (!addr) return ui.toast(str(t, 'errWdAddr'), 'error');
  if (!ADDR_RE[w.wdNet].test(addr)) return ui.toast(str(t, 'errWdAddrFormat'), 'error');
  const amt = parseDecimalInput(w.wdAmount, WD_DP);
  const bal = micro(w.balance) ?? 0n;
  const minM = micro(minWd) ?? 0n;
  if (!amt || cmp(toMicro(amt), minM) < 0 || cmp(toMicro(amt), bal) > 0) return ui.toast(interp(str(t, 'errWdAmount'), vars), 'error');
  const needTotp = !!s.sh.profile?.totp_enabled;
  if (needTotp && !new RegExp(`^\\d{${TOTP_LEN}}$`).test(w.wdTotp)) {
    setW(ctx, { wdTotp: '' });
    return ui.toast(str(t, 'err2fa'), 'error');
  }
  if (ctx.gp.gate.guard()) return; // U-24：未开通邮箱登录不提交提现，改弹引导
  const body = { network: w.wdNet, address: addr, amount: canonAmount(amt), ...(needTotp ? { totp: w.wdTotp } : {}) };
  await ui.run('wd', async () => {
    try {
      await ctx.gp.api.call('POST /api/user/wallet/withdrawals', { body });
      setW(ctx, { wdAddr: '', wdAmount: '', wdTotp: '' });
      scheduleWdQuote(ctx);
      ui.toast(str(t, 'toastWd'), 'success');
      await Promise.all([loadWds(ctx), loadWallet(ctx)]);
    } catch (e) {
      // 动态码 30s 内不可复用：任何提交失败（1006 以及 1105 / 1101 / 1102 等）均清空，避免用户以原码重提必得 1006
      if (needTotp) setW(ctx, { wdTotp: '' });
      ui.toast(ui.err(e, vars), 'error');
    }
  });
}

function openLog(ctx: H5Ctx, r: LedgerRow): void {
  const t = ctx.t;
  const [stKey] = LOG_STATUS[r.status] ?? LOG_STATUS.pending;
  const rows: Array<readonly [string, string]> = [
    [str(t, 'type'), ledgerTitle(t, r)],
    [str(t, 'amount'), ledgerAmount(r)],
    [str(t, 'time'), fmtDateTime(r.created_at, { seconds: true })],
    [str(t, 'status'), str(t, stKey)],
    ['ID', r.biz_no],
  ];
  const owed = owedOf(r);
  if (owed !== null) rows.push([str(t, 'feeOwedLabel'), owed + ' USDT']);
  // U-18：交易手续费扣款行（kind=fee 且非欠费抵扣）rows 下方注 feeNoRefundTip（S-45）
  const note = r.kind === 'fee' && !FEE_TPL_NO_TX_NOTE.has(r.title_tpl ?? '') ? str(t, 'feeNoRefundTip') : '';
  ctx.gp.ui.openModal({ type: 'info', title: str(t, 'logDetail'), rows, note });
}

function mask(v: string, head = 6): string {
  return v.length > head + 6 ? `${v.slice(0, head)}…${v.slice(-4)}` : v;
}

// 充值订单状态 → [文案键, 颜色]（U-07「充币」页签）
const ORDER_STATE: Readonly<Record<TopupOrderState, readonly [string, string]>> = {
  pending: ['ordPending', '#c47a0c'], paid: ['ordPaid', '#0d9a68'], expired: ['ordExpired', '#d1434b'],
  confirming: ['ordConfirming', '#c47a0c'], manual_review: ['ordReview', '#c47a0c'], rejected: ['ordRejected', '#d1434b'],
};

function orderRemaining(o: TopupOrderRow): number {
  return o.state === 'pending' && o.deadline_ms ? Math.max(0, Math.ceil((o.deadline_ms - Date.now()) / 1000)) : 0;
}

function orderRowVals(ctx: H5Ctx, o: TopupOrderRow): Record<string, unknown> {
  const t = ctx.t;
  const dp = tailDp(true, o.precision_digits);
  const left = orderRemaining(o);
  // 列表渲染时刻已过期但服务端仍记 pending：按已过期展示（下次刷新即为 expired）
  const state: TopupOrderState = o.state === 'pending' && left === 0 ? 'expired' : o.state;
  const [key, color] = ORDER_STATE[state] ?? ORDER_STATE.pending;
  const status = state === 'pending' ? interp(str(t, 'ordPendingLeft'), { min: String(Math.max(1, Math.ceil(left / 60))) }) : str(t, key);
  return {
    icon: ICON.deposit,
    iconStyle: 'width:38px;height:38px;border-radius:12px;border:1px solid #e6ebe8;display:inline-flex;align-items:center;justify-content:center;font-weight:800;color:#0f1a17;flex:none',
    title: interp(str(t, 'lgDeposit'), { network: o.network }),
    time: fmtDateTime(o.created_at, { seconds: true }),
    // 已到账显示实际入账，与「全部」页签同一笔 deposit 流水同口径（保留真实位数，用户拍板 2026-10-15）；其余显示订单应付精确金额
    amount: state === 'paid' && o.credited_usd ? '+' + m6(o.credited_usd) + ' USDT' : payAmount(o.amount_usdt, dp) + ' USDT',
    status,
    statusStyle: `font-size:11px;margin-top:2px;color:${color}`,
    hasOwed: false,
    owedText: '',
    open: () => openTopupOrder(ctx, o, state),
  };
}

// 待支付 → 回原支付页（同一订单：地址 / 精确金额 / 倒计时 / 二维码不变）；在途 → 订单页阶段视图；其余 → 只读详情
function openTopupOrder(ctx: H5Ctx, o: TopupOrderRow, state: TopupOrderState): void {
  const t = ctx.t;
  // 待支付与在途（确认中 / 处理中 / 异常）回订单页按当前阶段渲染；已到账 / 已过期只读详情
  if (state === 'pending' || state === 'confirming' || state === 'manual_review' || state === 'rejected') {
    const key = orderStorageKey(ctx);
    if (key) localStorage.setItem(key, String(o.id));
    clearPendingDepositOrder(ctx);
    if (ctx.host.state.w.depOrder?.id !== o.id) setW(ctx, { depOrder: null, depQuote: null, qr: '' });
    ctx.gp.ui.go('deposit');
    return;
  }
  const dp = tailDp(true, o.precision_digits);
  const rows: Array<[string, string]> = [
    [str(t, 'ordNetwork'), 'USDT-' + o.network],
    [str(t, 'ordAmount'), payAmount(o.amount_usdt, dp) + ' USDT'],
    [str(t, 'ordStatus'), str(t, (ORDER_STATE[state] ?? ORDER_STATE.expired)[0])],
    [str(t, 'ordCreated'), fmtDateTime(o.created_at, { seconds: true })],
  ];
  if (o.received_usdt) rows.push([str(t, 'ordReceived'), payAmount(o.received_usdt, dp) + ' USDT']);
  if (state === 'paid' && o.credited_usd) rows.push([str(t, 'ordCredited'), m6(o.credited_usd) + ' USDT']);
  const note = state === 'expired' ? str(t, 'ordExpiredNote') : '';
  ctx.gp.ui.openModal({ type: 'info', title: str(t, 'ordDetail'), rows, note });
}

function walletVals(ctx: H5Ctx): Record<string, unknown> {
  const { s, t } = ctx;
  const w = s.w;
  const owed = micro(owedNow(s)) ?? 0n;
  const feeOwedOn = owed > 0n;
  const ledger = w.ledger;
  const items = ledger?.items ?? [];
  const orders = w.tab === 'deposit' ? w.depOrders : null;
  const lf = listFlags(orders ?? ledger, PAGE_SIZE);
  return {
    isWallet: s.screen === 'wallet',
    usdtBalance: m2(w.balance),
    feeOwedOn,
    feeOwedText: interp(str(t, 'feeOwedTip'), { fee_owed: m2(owedNow(s)) }),
    walletFilters: TABS.map(([k, label]) => ({
      label: str(t, label),
      pick: () => void loadLedger(ctx, k),
      style: `flex:1;height:40px;border:0;background:transparent;font-weight:700;font-size:14px;cursor:pointer;color:${w.tab === k ? '#0f1a17' : '#8a958f'};border-bottom:2px solid ${w.tab === k ? '#0d9a68' : 'transparent'}`,
    })),
    visibleWalletLog: orders ? orders.items.map((o) => orderRowVals(ctx, o)) : items.map((r) => {
      const [stKey, color] = LOG_STATUS[r.status] ?? LOG_STATUS.pending;
      const owedR = owedOf(r);
      return {
        icon: ICON[r.kind] ?? '·',
        iconStyle: 'width:38px;height:38px;border-radius:12px;border:1px solid #e6ebe8;display:inline-flex;align-items:center;justify-content:center;font-weight:800;color:#0f1a17;flex:none',
        title: ledgerTitle(t, r),
        time: fmtDateTime(r.created_at, { seconds: true }),
        amount: ledgerAmount(r),
        status: str(t, stKey),
        statusStyle: `font-size:11px;margin-top:2px;color:${color}`,
        hasOwed: owedR !== null,
        owedText: owedR === null ? '' : interp(str(t, 'feeOwedAdded'), { owed: owedR }),
        open: () => openLog(ctx, r),
      };
    }),
    ledgerSkel: lf.skel,
    walletLogEmpty: lf.empty,
    ledgerRetry: lf.retry,
    retryLedger: () => void loadLedger(ctx, ctx.host.state.w.tab),
    logHasMore: lf.more,
    loadMoreLog: () => void loadMoreOf(ctx, 'logMore', (x) => (x.tab === 'deposit' ? x.depOrders : x.ledger)),
    busyLogMore: s.busy === 'logMore',
    logNoMore: lf.noMore,
  };
}

const MASKED_AMOUNT = '••••••';

// O-25：地址遮蔽为「前 4…后 4」（到期后仍可凭尾号核对，但无法整段复制付款）
function maskAddr(v: string): string {
  return v.length > 8 ? `${v.slice(0, 4)}••••${v.slice(-4)}` : '••••';
}

// O-25：精确金额拆为「整数与小数点」+「小数尾数」，尾数红色强调（共享订单须含全部小数精确支付）；非共享不拆分
function splitTail(amount: string, emphasize: boolean): [string, string] {
  const dot = amount.indexOf('.');
  if (!emphasize || dot < 0) return [amount, ''];
  return [amount.slice(0, dot + 1), amount.slice(dot + 1)];
}

// 充币页汇总区「充值手续费率」行：depositRate（用户生效价优先，缺省回落平台价）去尾零（2.000000 → 2%）；缺省 / 畸形 / 越界显示 —
function depFeeRateRow(s: H5State): string {
  const r = depositRate(s);
  return r === null ? '—' : pct(r) + '%';
}

function depositVals(ctx: H5Ctx): Record<string, unknown> {
  const { s, t } = ctx;
  const w = s.w;
  const closed = s.sh.config?.switches.deposit === false;
  const activeNet = w.depNetworks.find((n) => n.network === w.net);
  const quote = w.depOrder?.quote ?? w.depQuote;
  const minDep = activeNet?.min_deposit_usdt ?? limit(s, 'min_deposit', '10');
  const remaining = w.depOrder ? Math.max(0, Math.ceil((Date.parse(w.depOrder.expires_at) - Date.now() - rt(ctx.host).depOffset) / 1000)) : 0;
  const countdown = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`;
  // 订单页只展示本订单匹配的那笔入账（2026-10-15：不再列「此地址近期到账」，共享地址多人共用）
  const own = w.depOrder?.recent_deposits?.find((d) => d.id === w.depOrder?.deposit_id) ?? null;
  const shared=w.depOrder?.address_mode==='shared' || activeNet?.address_mode==='shared';
  const dp = tailDp(shared, w.depOrder?.precision_digits ?? quote?.precision_digits);
  // 三阶段（2026-10-17）：awaiting 原支付页；detected / processing / abnormal 已收到付款视图；credited 成功视图
  const phase = orderPhase(w.depOrder);
  const awaiting = !!w.depOrder && phase === 'awaiting';
  const canPay=awaiting && !!w.depOrder && (shared ? w.depOrder.status==='pending' && remaining>0 : w.depOrder.status!=='paid');
  // O-25：共享订单到期（或已非待支付）立即遮蔽地址与精确金额，防止到期后继续付款；已到账订单照常展示。
  // 已检测到付款（含异常）起一律遮蔽，防止重复付款
  const masked = !!w.depOrder && ((!awaiting && phase !== 'credited') || (shared && !canPay && w.depOrder.status !== 'paid'));
  const amountText = w.depOrder ? payAmount(w.depOrder.amount_usdt, dp) : '';
  const [amtHead, amtTail] = splitTail(amountText, shared && !masked);

  return {
    isDeposit: s.screen === 'deposit',
    backFromDeposit: () => ctx.gp.ui.go(ctx.host.state.sh.navFrom === 'home' ? 'home' : 'wallet'),
    networks: DEP_NETS.map((n) => ({
      label: 'USDT-' + n,
      pick: () => { if (w.depNetworks.find((x) => x.network === n)?.available) chooseDepositNetwork(ctx, n); },
      style: `flex:1;height:40px;border:1px solid ${w.net === n ? '#0d9a68' : '#dfe5e2'};border-radius:12px;background:${w.net === n ? '#0f1a17' : '#fff'};color:${w.net === n ? '#fff' : '#0f1a17'};font-weight:700;font-size:13px;cursor:${w.depNetworks.find((x) => x.network === n)?.available ? 'pointer' : 'not-allowed'};opacity:${w.depNetworks.find((x) => x.network === n)?.available ? 1 : 0.45}`,
    })),
    depositClosed: closed,
    depositClosedText: str(t, 'errSwitchOff'),
    qrSrc: w.qr,
    hasQr: canPay && !!w.qr,
    depCanPay:canPay,
    depIsShared:shared,
    depExpiredText:str(t,shared?'depSharedExpired':'depExpired'),
    depExactWarning: str(t,'depExactWarning'),
    depUnavailableText: activeNet?.available ? '' : str(t, depReasonKey(activeNet?.reason)),
    // 复制与展示同一定长串（精确应付金额，不含千分位）
    copyDepositAmount:()=>{if(canPay && amountText && ctx.host.state.w.depOrder?.id===w.depOrder?.id)ctx.gp.ui.copy(amountText);},
    depositAddress: masked ? maskAddr(w.depOrder?.address ?? '') : w.depOrder?.address ?? '',
    depAmountMasked: masked,
    depMaskedText: masked ? str(t, 'depMasked') : '',
    depTransferHead: masked ? MASKED_AMOUNT : amtHead,
    depTransferTail: masked ? '' : amtTail,
    depTailHint: shared && canPay ? str(t, 'depTailHint') : '',
    depQuoteTailNote: shared && !w.depOrder && !!w.depQuote ? str(t, 'depQuoteTailNote') : '',
    depAmountPh: shared ? '0.00' : '0.000000',
    copyAddress: () => {
      const a = ctx.host.state.w.depOrder?.address;
      if (a && canPay) ctx.gp.ui.copy(a);
    },
    depositWarnText: interp(str(t, shared ? 'depositWarnShared' : 'depositWarn'), { min_deposit: lim(minDep) }),
    minDepositText: trimZeros(minDep) + ' USDT', // 「1.000000」→「1 USDT」
    depConfirmLabel: str(t, 'depConfirmLabel'),
    depFeeRateRowText: depFeeRateRow(s),
    networkConfirm: String(quote?.confirmations ?? activeNet?.confirmations ?? ''),
    depFeeRateText: quote ? pct(quote.fee_rate) + '%' : '—',
    depFeeRateStyle: 'color:#0f1a17',
    depAmountLabel: str(t, shared?'depSharedAmountLabel':'depAmountLabel'),
    depAmount: w.depAmount,
    onDepAmount: (e: InputEvent) => editDepositAmount(ctx, e.target.value),
    showDepPreview: !!quote,
    depEstFeeLabel: str(t, 'depEstFee'),
    depEstFeeText: quote ? quoteAmt(quote.fee_usd) : '',
    depEstNetLabel: str(t, 'depEstNet'),
    depEstNetText: quote ? quoteAmt(quote.net_usd) : '',
    depLowWarn: !w.depOrder && depositInputAmount(w.depAmount) !== null && (depositInputAmount(w.depAmount) ?? 0n) > 0n && (depositInputAmount(w.depAmount) ?? 0n) < (micro(minDep) ?? 0n),
    depLowText: interp(str(t, 'depLowWarn'), { min_deposit: lim(minDep) }),
    depHasOrder: !!w.depOrder,
    depHasQuote: !!w.depQuote,
    depCanQuote: !!activeNet?.available && !w.depOrder,
    // 用户拍板 2026-10-15：钱包是 USDT，applied_rate 是 USDT→USDT 兑换汇率（D36 G = A × r；有汇率差时为代理生效价），rate = 1 也显示，不标 USD
    depRateText: quote ? interp(str(t, 'depRateValue'), { rate: trimZeros(quote.applied_rate) }) : '',
    depGrossText: quote ? quoteAmt(quote.gross_usd) : '',
    depTransferText: masked ? MASKED_AMOUNT : amountText,
    depCountdown: countdown,
    depOrderExpired: !!w.depOrder && remaining === 0,
    depOrderStatus: w.depOrder ? str(t, `depSt_${w.depOrder.status}`) || w.depOrder.status : '',
    depOrderCreditOn: awaiting && own !== null,
    depOrderCreditText: own ? interp(str(t, own.status === 'credited' ? 'depOrderCredited' : 'depOrderProcessing'), {
      amount: payAmount(own.amount_usdt, dp), net: quoteAmt(own.net_usd), status: str(t, `depDst_${own.status}`) || own.status,
    }) : '',
    requestDepositQuote: () => void requestDepositQuote(ctx),
    confirmDepositOrder: () => void confirmDepositOrder(ctx),
    newDepositOrder: () => resetDepositOrder(ctx),
    depBusyQuote: s.busy === 'depQuote',
    depBusyOrder: rt(ctx.host).depBusy,
    ...depPhaseVals(ctx, phase, dp),
  };
}

// 订单页三阶段视图值（2026-10-17）：② 已收到付款（确认中 / 处理中 / 异常）与 ③ 充值成功；① 待支付沿用上方原支付页键
function depPhaseVals(ctx: H5Ctx, phase: TopupPhase, dp: number): Record<string, unknown> {
  const { s, t } = ctx;
  const o = s.w.depOrder;
  const pay = o?.payment ?? null;
  const received = pay?.received_usdt ? payAmount(pay.received_usdt, dp) : '';
  const need = pay?.required_confirmations ?? o?.quote.confirmations ?? 0;
  const conf = Math.min(pay?.confirmations ?? 0, need);
  const progress = !!o && (phase === 'detected' || phase === 'processing' || phase === 'abnormal');
  const success = !!o && phase === 'credited';
  const abnormal = phase === 'abnormal';
  const hash = pay?.tx_hash ?? '';
  let progressText = '';
  if (phase === 'detected') progressText = interp(str(t, 'depPhDetected'), { amount: received, conf, need });
  else if (phase === 'processing') progressText = interp(str(t, 'depPhProcessing'), { amount: received });
  else if (abnormal) progressText = str(t, pay?.abnormal_reason === 'amount_mismatch' ? 'depPhMismatch' : 'depPhAbnormal');
  const pct = need > 0 ? Math.round((conf / need) * 100) : 0;
  const creditedAt = pay?.credited_at ?? '';
  const copyHash = (): void => {
    const h = ctx.host.state.w.depOrder?.payment?.tx_hash;
    if (h) ctx.gp.ui.copy(h);
  };
  return {
    depShowNets: !o || phase === 'awaiting',
    depShowPay: !!o && phase === 'awaiting',
    depShowProgress: progress,
    depProgressTitle: progress ? str(t, abnormal ? 'depPhAbnormalTitle' : 'depPhDetectedTitle') : '',
    depProgressText: progressText,
    depProgressStyle: `width:100%;border-radius:12px;padding:12px;font-size:13px;font-weight:700;line-height:1.6;${abnormal ? 'background:#fff3df;color:#8a5a0c' : 'background:#e3f6ee;color:#0d9a68'}`,
    depProgressBarOn: phase === 'detected' && need > 0,
    depProgressBarStyle: `height:100%;width:${pct}%;background:#0d9a68;border-radius:999px;transition:width .4s ease`,
    depProgressNote: progress ? str(t, abnormal ? 'depPhAbnormalNote' : 'depPhNoRepeat') : '',
    depHashOn: hash !== '',
    depHashLabel: str(t, 'depOkHash'),
    depHashText: hash ? mask(hash, HASH_HEAD) : '',
    copyDepHash: copyHash,
    depShowSuccess: success,
    depOkTitle: str(t, 'depOkTitle'),
    depOkRows: success && pay ? [
      { label: str(t, 'depOkReceived'), value: received + ' USDT', style: 'color:#0f1a17' },
      { label: str(t, 'depOkFee'), value: quoteAmt(pay.fee_usd ?? '0') + ' USDT', style: 'color:#0f1a17' },
      { label: str(t, 'depOkNet'), value: quoteAmt(pay.net_usd ?? '0') + ' USDT', style: 'color:#0d9a68' },
      { label: str(t, 'depOkTime'), value: creditedAt ? fmtDateTime(creditedAt, { seconds: true }) : '—', style: 'color:#0f1a17' },
    ] : [],
    depOkGoCardText: str(t, 'depOkGoCard'),
    depOkBalanceText: str(t, 'depOkBalance'),
    depOkAgainText: str(t, 'depOkAgain'),
    goCardFromDeposit: () => { if (!ctx.gp.nav.openProducts()) ctx.gp.ui.go('products'); },
    viewBalanceFromDeposit: () => ctx.gp.ui.go('wallet'),
    // 「创建新充值订单」：待支付（含已到期）与异常态可放弃本单；确认中 / 处理中不提供，防止误以为未到账而重复付款
    depShowNewOrder: !!o && (phase === 'awaiting' || abnormal),
    depShowWarn: !success,
  };
}

function withdrawVals(ctx: H5Ctx): Record<string, unknown> {
  const { s, t, lang } = ctx;
  const w = s.w;
  const amtStr = parseDecimalInput(w.wdAmount, WD_DP);
  const amt = amtStr ? toMicro(amtStr) : 0n;
  // 预检确定（ok）时手续费 / 实际到账以服务端为准（与建单同一计算）；不确定或无结果时按本地基础费率计算（不含激活费，激活费另行提示）
  const q = currentWdQuote(s);
  const qFee = q !== null && q.activation_check === 'ok' ? micro(q.fee) : null;
  const fee = qFee ?? wdFee(s, w.wdAmount);
  const rate = withdrawRate(s);
  const rateM = micro(rate);
  const wds = w.wds;
  const items = wds?.items ?? [];
  const wf = listFlags(wds, PAGE_SIZE);
  const colon = lang === 'zh' ? '：' : ': ';
  return {
    isWithdrawUsdt: s.screen === 'withdrawUsdt',
    wdNets: WD_NETS.map((n) => ({
      label: 'USDT-' + n,
      pick: () => { setW(ctx, { wdNet: n }); scheduleWdQuote(ctx); },
      style: `flex:1;height:40px;border:1px solid ${w.wdNet === n ? '#0d9a68' : '#dfe5e2'};border-radius:12px;background:${w.wdNet === n ? '#0f1a17' : '#fff'};color:${w.wdNet === n ? '#fff' : '#0f1a17'};font-weight:700;font-size:13px;cursor:pointer`,
    })),
    wdAddr: w.wdAddr,
    wdAddrPh: ADDR_PH[w.wdNet],
    onWdAddr: (e: InputEvent) => { setW(ctx, { wdAddr: e.target.value }); scheduleWdQuote(ctx); },
    wdAmount: w.wdAmount,
    onWdAmount: (e: InputEvent) => { setW(ctx, { wdAmount: e.target.value }); scheduleWdQuote(ctx); },
    wdAll: (e: ClickEvent) => {
      e.preventDefault();
      const b = micro(ctx.host.state.w.balance);
      setW(ctx, { wdAmount: b === null ? '' : m6(b).replace(/,/g, '') });
      scheduleWdQuote(ctx);
    },
    wdFeeText: m6(fee),
    wdGetText: m6(mmax(0n, sub(amt, fee))),
    wdFeeNote: wdNotes(s, t, rateM, rate, amt > 0n ? mmax(0n, sub(sub(amt, fee), wdActFee(s))) : null),
    wdActTip: wdActTip(t, q),
    wdActTipStyle: q !== null && q.activation_check === 'unknown'
      ? 'margin:0;background:#f3f6f5;border:1px solid #dfe5e2;border-radius:12px;padding:10px 12px;font-size:12px;color:#4b5853;line-height:1.6'
      : 'margin:0;background:#fff3df;border:1px solid #f5dfae;border-radius:12px;padding:10px 12px;font-size:12.5px;font-weight:600;color:#8a5a0c;line-height:1.6',
    minWithdrawText: interp(str(t, 'wdMin'), { min: lim(limit(s, 'min_withdraw', '20')) }),
    showWdTotp: !!s.sh.profile?.totp_enabled,
    wdTotpLabel: str(t, 'wdTotpLabel'),
    wdTotp: w.wdTotp,
    onWdTotp: (e: InputEvent) => setW(ctx, { wdTotp: e.target.value.replace(/\D/g, '').slice(0, TOTP_LEN) }),
    wdOwedText: interp(str(t, 'errFeeOwed'), { fee_owed: m2(owedNow(s)) }),
    withdrawClosed: s.sh.config?.switches.withdraw === false,
    withdrawClosedText: str(t, 'errSwitchOff'),
    submitWd: () => void submitWd(ctx),
    busyWd: s.busy === 'wd',
    wdList: items.map((r) => {
      const [key, bg, fg] = WD_STATUS[r.status] ?? WD_STATUS.pending_review;
      const note = r.review_note?.trim() ?? '';
      return {
        amount: m6(r.amount),
        net: r.network,
        addr: mask(r.to_address),
        hash: r.tx_hash ? mask(r.tx_hash, 4) : '—', // 原型 L563「8a2f…4c11」
        time: fmtDateTime(r.created_at, { seconds: true }),
        statusText: str(t, key),
        statusStyle: `font-size:11px;border-radius:999px;padding:3px 9px;font-weight:700;background:${bg};color:${fg}`,
        hasReject: r.status === 'rejected',
        rejectText: r.status !== 'rejected' ? '' : note ? `${str(t, 'wdRejectReason')}${colon}${note} · ${str(t, 'wdRefunded')}` : str(t, 'wdRefunded'),
      };
    }),
    wdSkel: wf.skel,
    wdEmpty: wf.empty,
    wdRetry: wf.retry,
    retryWd: () => void loadWds(ctx),
    wdHasMore: wf.more,
    loadMoreWd: () => void loadMoreOf(ctx, 'wdMore', (x) => x.wds),
    busyWdMore: s.busy === 'wdMore',
    wdNoMore: wf.noMore,
  };
}

function refetchScreen(ctx: H5Ctx): void {
  const s = ctx.host.state;
  if (s.screen === 'wallet') {
    void loadWallet(ctx);
    void loadLedger(ctx, s.w.tab);
  } else if (s.screen === 'deposit') {
    void loadDepositNetworks(ctx);
    if (s.w.depOrder) void refreshDepositOrder(ctx, s.w.depOrder.id);
  } else if (s.screen === 'withdrawUsdt') {
    void loadWallet(ctx);
    if (!rt(ctx.host).poll) void loadWds(ctx); // 有在途记录时轮询器回前台已立即补跑第 1 页
  }
}

export const wallet: H5Domain = {
  name: 'wallet',
  keys: KEYS,
  modalKeys: MODAL_KEYS,
  migrated: true,
  i18n: I18N_WALLET,
  rewrites: REWRITES_WALLET,
  state: () => ({ w: W0 }),
  mount(ctx) {
    const reset = (): void => {
      const r = rt(ctx.host);
      r.gen++;
      r.depRequest++;
      r.depKey = '';
      r.depBusy = false;
      stopPoll(ctx.host);
      stopDepositTimer(ctx.host);
      cancelWdQuote(ctx.host);
      ctx.host.setState({ w: W0 });
    };
    ctx.events.on('session:ready', () => {
      reset();
      void loadWallet(ctx); // 首页 / 开卡表单读 usdtBalance
    });
    ctx.events.on('session:lost', reset);
    // U-22 E-05：回前台立即重拉当前页全部查询（钱包 / 充币 / 提币页无定时轮询的查询；提币记录轮询由 createPoller 自身回前台补跑）
    const r = rt(ctx.host);
    r.unFg?.();
    r.unFg = ctx.gp.foreground.on(() => refetchScreen(ctx));
    // 06 U-12 通知跳转：deposit_credited / fee_charged 进 U-07 指定页签
    ctx.gp.nav.provide({
      openLedger: (tab) => {
        if (ctx.host.state.screen === 'wallet') void loadLedger(ctx, tab);
        else {
          setW(ctx, { tab });
          ctx.gp.ui.go('wallet');
        }
        return true;
      },
    });
  },
  update(ctx, prev) {
    const to = ctx.s.screen;
    if (prev.screen === to) return;
    if (to === 'wallet') {
      void loadWallet(ctx);
      void loadLedger(ctx, ctx.s.w.tab);
    } else if (to === 'deposit') {
      void openDeposit(ctx);
    } else if (to === 'withdrawUsdt') {
      void loadWallet(ctx);
      void loadWds(ctx);
    }
    if (prev.screen === 'withdrawUsdt') manageWdPoll(ctx);
    if (prev.screen === 'deposit') stopDepositTimer(ctx.host);
  },
  unmount(ctx) {
    stopPoll(ctx.host);
    stopDepositTimer(ctx.host);
    cancelWdQuote(ctx.host);
    const r = rt(ctx.host);
    r.unFg?.();
    r.unFg = null;
  },
  vals: (ctx) => ({ ...walletVals(ctx), ...depositVals(ctx), ...withdrawVals(ctx) }),
};

export const __test = { orderPhase, depPollSecs, successExpired, ledgerTitle, wdFee, depositRate, depFeeRateRow, feeRowsOf, defaultFeeRowsOf, owedOf, ledgerAmount, pct, mask, WD_STATUS, ICON };
